import type { FC, ReactNode } from 'react'
import type { Principle } from '@/constants/content'
import type { PixelPalette } from '@/lib/pixel'

import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { PixelSprite } from '@/components/pixel/pixel-sprite'

import RetroWindow from '@/components/generals/retro-window'
import TypedHeading from '@/components/landing/typed-heading'
import PixelCloud from '@/components/pixel/pixel-cloud'

import { PRINCIPLES, WHO_WE_ARE } from '@/constants/content'
import { ICON_CHECK, ICON_HEART, ICON_SPARKLE } from '@/constants/sprites'

const PRINCIPLE_ICONS: Record<Principle['icon'], ReadonlyArray<string>> = {
    sparkle: ICON_SPARKLE,
    check: ICON_CHECK,
    heart: ICON_HEART
}

const PRINCIPLE_TILES: Record<Principle['icon'], string> = {
    sparkle: 'bg-bubblegum',
    check: 'bg-fern',
    heart: 'bg-white'
}

const HEART_PALETTE: PixelPalette = { K: 'var(--bubblegum-deep)' }

const TILTS = ['lg:-rotate-2', 'lg:rotate-1', 'lg:-rotate-1']

const WhoWeAreSection: FC = (): ReactNode => {
    return (
        <section id='who-we-are' aria-labelledby='who-title' className='relative py-20 md:py-28'>
            <PixelCloud shape='small' className='pointer-events-none absolute top-8 right-[8%] w-28 animate-drift [--drift:-40px] md:w-40' />

            <div className='mx-auto max-w-7xl px-5 md:px-8'>
                <TypedHeading
                    id='who-title'
                    text={WHO_WE_ARE.heading}
                    className='font-heading text-[clamp(2.75rem,12vw,11rem)] leading-[0.85] tracking-tight'
                />

                <div className='mt-12 grid items-start gap-12 lg:mt-16 lg:grid-cols-[1.2fr_1fr] lg:gap-16'>
                    <Reveal variant='left'>
                        <RetroWindow title={WHO_WE_ARE.fileName} bodyClassName='space-y-5 md:space-y-6'>
                            {WHO_WE_ARE.paragraphs.map(paragraph => (
                                <p key={paragraph} className='text-base leading-relaxed font-medium md:text-lg'>
                                    {paragraph}
                                </p>
                            ))}

                            <p className='flex items-center gap-2 font-heading text-lg text-bubblegum-deep'>
                                <span aria-hidden='true'>&gt;</span>
                                {WHO_WE_ARE.signature}
                                <span aria-hidden='true' className='inline-block h-5 w-2.5 animate-caret bg-ink' />
                            </p>
                        </RetroWindow>
                    </Reveal>

                    <ul className='grid gap-6'>
                        {PRINCIPLES.map((principle, index) => (
                            <Reveal as='li' key={principle.title} variant='right' delay={index * 130}>
                                <div
                                    className={cn(
                                        'flex gap-4 border-2 border-ink bg-white p-5 shadow-pixel transition-[rotate,translate] duration-200 ease-[steps(3,end)] hover:-translate-y-1 hover:rotate-0 md:gap-5 md:p-6',
                                        TILTS[index]
                                    )}
                                >
                                    <span
                                        aria-hidden='true'
                                        className={cn('grid size-12 shrink-0 place-items-center border-2 border-ink md:size-14', PRINCIPLE_TILES[principle.icon])}
                                    >
                                        <PixelSprite
                                            rows={PRINCIPLE_ICONS[principle.icon]}
                                            palette={principle.icon === 'heart' ? HEART_PALETTE : undefined}
                                            className='h-6 w-auto md:h-7'
                                        />
                                    </span>

                                    <div>
                                        <h3 className='font-heading text-2xl leading-none md:text-3xl'>
                                            {principle.title}
                                        </h3>

                                        <p className='mt-2 text-sm leading-relaxed font-medium md:text-base'>
                                            {principle.text}
                                        </p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    )
}

export default WhoWeAreSection
